import { addBusinessDays, addDays } from '../date';

type Token = string | number;

function tokenize(formula: string): Token[] {
	return formula.match(/\w+|\(|\)|\+|\-|\*|\/|\=/g) || [];
}

class Parser {
	private tokens: Token[];

	constructor(tokens: Token[]) {
		this.tokens = tokens;
	}

	parse(): any {
		if (this.tokens[0] === '=') {
			this.tokens.shift();
		}
		return this.parseExpression();
	}

	private parseExpression(): any {
		let result = this.parseTerm();
		while (this.tokens.length && ['+', '-'].includes(this.tokens[0] as string)) {
			const op = this.tokens.shift() as string;
			const term = this.parseTerm();
			result = op === '+' ? result + term : result - term;
		}
		return result;
	}

	private parseTerm(): any {
		let result = this.parseFactor();
		while (this.tokens.length && ['*', '/'].includes(this.tokens[0] as string)) {
			const op = this.tokens.shift() as string;
			const factor = this.parseFactor();
			result = op === '*' ? result * factor : result / factor;
		}
		return result;
	}

	private parseFactor(): any {
		if (this.tokens[0] === '(') {
			this.tokens.shift(); // Remove opening parenthesis
			const result = this.parseExpression();
			this.tokens.shift(); // Remove closing parenthesis
			return result;
		} else if (
			typeof this.tokens[0] === 'number' ||
			(typeof this.tokens[0] === 'string' && !isNaN(Number(this.tokens[0])))
		) {
			return Number(this.tokens.shift());
		} else {
			return this.parseFunction();
		}
	}

	private parseFunction(): any {
		const funcName = this.tokens.shift() as string;
		this.tokens.shift(); // Remove opening parenthesis
		const args: any[] = [];
		while (this.tokens[0] !== ')') {
			args.push(this.parseExpression());
			if (this.tokens[0] === ',') {
				this.tokens.shift();
			}
		}
		this.tokens.shift(); // Remove closing parenthesis
		return evaluateFunction(funcName, args);
	}
}

function evaluateFunction(funcName: string, args: any[]): any {
	switch (funcName) {
		case 'TODAY':
			return new Date();
		case 'DATE_FORMAT':
			return formatDate(args[0]);
		case 'ADD_DAYS':
			if (args.length !== 2) {
				throw new Error('ADD_DAYS function requires 2 arguments');
			}
			return addDays(args[0], args[1]);
		case 'ADD_BUSINESS_DAYS':
			if (args.length !== 2) {
				throw new Error('ADD_BUSINESS_DAYS function requires 2 arguments');
			}
			return addBusinessDays(args[0], args[1]);
		default:
			throw new Error(`Unknown function: ${funcName}`);
	}
}

function formatDate(str: Date | string): string {
	const date = typeof str === 'string' ? new Date(str) : str;

	return date.toDateString();
}

export function evaluate(formula: string): any {
	const tokens = tokenize(formula);
	const parser = new Parser(tokens);

	return parser.parse();
}
